/**
 * React hook for handling player-related WebSocket events.
 */

import { useEffect, useRef } from 'react'
import { useSocket } from './useSocket'
import { Player } from '@/stores/playerStore'

export interface PlayerJoinedData {
  player: {
    id: string
    name: string
    is_host: boolean
    is_ready: boolean
    role: 'phone' | 'tv'
  }
}

export interface PlayerLeftData {
  player_id: string
  reason: 'left' | 'timeout' | 'kicked'
}

export interface PlayerDisconnectedData {
  player_id: string
  timeout_seconds: number
}

export interface PlayerReconnectedData {
  player_id: string
}

export interface PlayerAwayData {
  player_id: string
}

export interface PlayerReturnedData {
  player_id: string
}

export interface PlayerReadyData {
  player_id: string
  is_ready: boolean
}

export interface UsePlayerEventsCallbacks {
  onPlayerJoined?: (player: Player) => void
  onPlayerLeft?: (playerId: string) => void
  onPlayerDisconnected?: (playerId: string, timeoutSeconds: number) => void
  onPlayerReconnected?: (playerId: string) => void
  onPlayerAway?: (playerId: string) => void
  onPlayerReturned?: (playerId: string) => void
  onPlayerReady?: (playerId: string, isReady: boolean) => void
}

export function usePlayerEvents(callbacks?: UsePlayerEventsCallbacks): void {
  const { on, isConnected } = useSocket()

  // Use ref to store callbacks to avoid resubscribing on every callback change
  const callbacksRef = useRef(callbacks)
  useEffect(() => {
    callbacksRef.current = callbacks
  }, [callbacks])

  // Subscribe to events when connected - stable handlers
  useEffect(() => {
    console.log('[PlayerEvents] useEffect triggered, isConnected:', isConnected)
    if (!isConnected) {
      console.log('[PlayerEvents] Not connected, skipping subscription')
      return
    }

    console.log('[PlayerEvents] Subscribing to player events...')
    
    // Stable handlers that use ref - no re-subscription needed
    const unsubJoined = on<PlayerJoinedData>('player:joined', (data) => {
      console.log('[PlayerEvents] player:joined', data)
      if (callbacksRef.current?.onPlayerJoined) {
        callbacksRef.current.onPlayerJoined({
          id: data.player.id,
          name: data.player.name,
          is_host: data.player.is_host,
          is_ready: data.player.is_ready,
          role: data.player.role || 'phone',
          connection_status: 'connected',
        })
      }
    })
    
    const unsubLeft = on<PlayerLeftData>('player:left', (data) => {
      console.log('[PlayerEvents] player:left', data)
      if (callbacksRef.current?.onPlayerLeft) {
        callbacksRef.current.onPlayerLeft(data.player_id)
      }
    })
    
    const unsubDisconnected = on<PlayerDisconnectedData>('player:disconnected', (data) => {
      console.log('[PlayerEvents] player:disconnected', data)
      if (callbacksRef.current?.onPlayerDisconnected) {
        callbacksRef.current.onPlayerDisconnected(data.player_id, data.timeout_seconds)
      }
    })
    
    const unsubReconnected = on<PlayerReconnectedData>('player:reconnected', (data) => {
      console.log('[PlayerEvents] player:reconnected', data)
      if (callbacksRef.current?.onPlayerReconnected) {
        callbacksRef.current.onPlayerReconnected(data.player_id)
      }
    })

    const unsubAway = on<PlayerAwayData>('player:away', (data) => {
      console.log('[PlayerEvents] player:away', data)
      if (callbacksRef.current?.onPlayerAway) {
        callbacksRef.current.onPlayerAway(data.player_id)
      }
    })

    const unsubReturned = on<PlayerReturnedData>('player:returned', (data) => {
      console.log('[PlayerEvents] player:returned', data)
      if (callbacksRef.current?.onPlayerReturned) {
        callbacksRef.current.onPlayerReturned(data.player_id)
      }
    })
    
    const unsubReady = on<PlayerReadyData>('player:ready', (data) => {
      console.log('[PlayerEvents] player:ready', data)
      if (callbacksRef.current?.onPlayerReady) {
        callbacksRef.current.onPlayerReady(data.player_id, data.is_ready)
      }
    })
    
    console.log('[PlayerEvents] Subscribed to all player events')

    return () => {
      console.log('[PlayerEvents] Cleanup - unsubscribing from player events')
      unsubJoined()
      unsubLeft()
      unsubDisconnected()
      unsubReconnected()
      unsubAway()
      unsubReturned()
      unsubReady()
    }
  }, [isConnected, on])
}
